import type { 
	ApiResponse, 
	DDNSStatusResponse,
	DomainsResponse,
	DNSRecordsResponse,
	DNSRecordResponse,
	DNSProvider,
	DNSRecordInput,
} from '@tools-set/shared-types';

// 根据环境自动切换 API Base URL
const getApiBase = (): string => {
        // 开发环境使用相对路径,通过 Vite proxy 转发到 localhost:8787
        if (import.meta.env.DEV) {
                return '/api';
        }
        // 生产环境直接请求域名
        return 'https://tools.lionely.top/api';
};

const API_BASE = getApiBase();

/**
 * 通用的 API 请求函数
 * 
 * 使用 Cloudflare Access 用户认证（Cookie-based）
 * 用户首次访问会被重定向到 Cloudflare Access 登录页
 * 登录后 CF_Authorization cookie 会自动携带
 */
export async function fetchApi<T>(endpoint: string, options?: RequestInit): Promise<ApiResponse<T>> {
        const url = `${API_BASE}${endpoint}`;

        const headers: Record<string, string> = {
                'Content-Type': 'application/json',
                ...(options?.headers as Record<string, string>),
        };

        const response = await fetch(url, {
                headers,
                credentials: 'include', // 携带 Cloudflare Access cookie (CF_Authorization)
                ...options,
        });

        if (!response.ok) {
                const errorText = await response.text().catch(() => 'Unknown error');
                throw new Error(`API Error: ${response.status} - ${errorText}`);
        }

        return response.json();
}

export const api = {
        // 数据相关
        getData: () => fetchApi<any[]>('/data'),
        getDataById: (id: string) => fetchApi<any>(`/data/${id}`),
        createData: (data: any) => fetchApi('/data', { method: 'POST', body: JSON.stringify(data) }),
        updateData: (id: string, data: any) => fetchApi(`/data/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
        deleteData: (id: string) => fetchApi(`/data/${id}`, { method: 'DELETE' }),

        // DDNS 相关
        fetchDDNSStatus: () => fetchApi<DDNSStatusResponse>('/ddns/ddnsStatus'),
        updateCloudflareRecord: (recordId: string) => fetchApi(`/ddns/updateCloudflareRecord`, { method: 'POST', body: JSON.stringify({ recordId }) }),

	// DNS 管理相关
	// 获取所有域名
	getDomains: () => fetchApi<DomainsResponse>('/dns/domains'),
	
	// 获取指定域名的 DNS 记录
	getDNSRecords: (provider: DNSProvider, domainId: string) => 
		fetchApi<DNSRecordsResponse>(`/dns/records/${provider}/${domainId}`),
	
	// 创建 DNS 记录
	createDNSRecord: (input: DNSRecordInput) => 
		fetchApi<DNSRecordResponse>('/dns/records', { 
			method: 'POST', 
			body: JSON.stringify(input) 
		}),
	
	// 更新 DNS 记录
	updateDNSRecord: (provider: DNSProvider, domainId: string, recordId: string, input: Partial<DNSRecordInput>) => 
		fetchApi<DNSRecordResponse>(`/dns/records/${provider}/${domainId}/${recordId}`, { 
			method: 'PUT', 
			body: JSON.stringify(input) 
		}),
	
	// 删除 DNS 记录
	deleteDNSRecord: (provider: DNSProvider, domainId: string, recordId: string) => 
		fetchApi(`/dns/records/${provider}/${domainId}/${recordId}`, { 
			method: 'DELETE' 
		}),
};
